import {
  FormControlLabel,
  Stack,
  Switch,
  SwitchProps,
  SxProps,
} from '@mui/material'
import { ReactNode } from 'react'
import {
  useController,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form'

import { PartialBy, safeCypressID } from '@/utils'
export type VailSwitchProps<T extends object> = PartialBy<
  UseControllerProps<T>,
  'control'
> &
  Omit<SwitchProps, 'onChange'> & {
    label: string
    stackProps?: SxProps
    onChange?: (checked: boolean) => void
    endAdornment?: ReactNode
    helperText?: string
  }

export const VailSwitch = <T extends {}>(props: VailSwitchProps<T>) => {
  const form = useFormContext<T>()
  if (!form) throw new Error('VailSwitch must be used inside a FormProvider')

  const {
    name,
    rules,
    shouldUnregister,
    defaultValue,
    disabled,
    control,
    helperText,
    ...switchProps
  } = props
  const { field } = useController({
    control: control || form.control,
    defaultValue,
    disabled,
    name,
    rules,
    shouldUnregister,
  })
  const { value, onChange } = field
  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={switchProps.stackProps}
    >
      <FormControlLabel
        control={
          <Switch
            id={safeCypressID(props.id ?? props.name)}
            slotProps={{
              input: {
                'aria-label': 'controlled',
                ref: field.ref,
              },
            }}
            sx={props.sx}
            {...field}
            checked={value ?? props.defaultValue ?? true}
            onChange={(e) => {
              onChange(e)
              return props.onChange?.(e.target.checked)
            }}
          />
        }
        label={props.label}
      />
      {props.endAdornment}
    </Stack>
  )
}
